import { PageContainer } from "../layout/PageContainer"
import { ActivityRowSkeleton } from "../ui/ActivityRowSkeleton"
import { PositionCardSkeleton } from "../ui/PositionCardSkeleton"
import { Skeleton } from "../ui/Skeleton"

interface ProfilePageSkeletonProps {
	activeTab: "positions" | "activity"
	statusFilter: "active" | "closed"
}

/**
 * Full page skeleton for profile pages.
 * Used during initial page load before data is available.
 */
export function ProfilePageSkeleton({ activeTab, statusFilter }: ProfilePageSkeletonProps) {
	return (
		<>
			<div className="border-border-subtle border-b bg-bg-card">
				<PageContainer className="py-4 sm:py-6">
					{/* Avatar and Name Row */}
					<div className="mb-4 flex items-center gap-3 sm:mb-6 sm:gap-4">
						<div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-tertiary sm:size-16">
							<Skeleton className="size-full rounded-full" />
						</div>
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-2">
								<Skeleton className="h-7 w-32 sm:h-8" />
								<Skeleton className="size-8 rounded-full" />
							</div>
						</div>
					</div>

					{/* Stats Grid - Card Layout */}
					<div className="grid grid-cols-3 gap-2 sm:gap-4">
						{[0, 1, 2].map(i => (
							<div
								className="rounded-2xl border border-border-subtle bg-gradient-to-br from-bg-secondary to-bg-card p-3 sm:p-5"
								key={`stat-${i}`}
							>
								{/* Icon + Label row */}
								<div className="mb-1 flex items-center gap-1.5 sm:mb-2">
									<Skeleton className="size-5 rounded-full sm:size-6" />
									<Skeleton className="h-3 w-10 sm:h-4 sm:w-14" />
								</div>
								{/* Value: text-xl sm:text-3xl */}
								<Skeleton className="h-7 w-16 sm:h-9 sm:w-24" />
								{/* Actions area */}
								<div className="mt-3">
									<Skeleton className="h-8 w-full rounded-lg sm:h-10" />
								</div>
							</div>
						))}
					</div>
				</PageContainer>

				{/* Main Tabs */}
				<PageContainer className="border-border-subtle border-b">
					<div className="flex gap-4 sm:gap-6" role="tablist">
						<Skeleton className="h-11 w-[76px]" />
						<Skeleton className="h-11 w-[62px]" />
					</div>
				</PageContainer>
			</div>

			{/* Content */}
			<PageContainer className="pt-4 pb-8 sm:pt-6">
				{activeTab === "positions" ? (
					<PositionsContentSkeleton statusFilter={statusFilter} />
				) : (
					<ActivityContentSkeleton />
				)}
			</PageContainer>
		</>
	)
}

interface PositionsContentSkeletonProps {
	statusFilter: "active" | "closed"
}

/**
 * Skeleton for positions tab content.
 * Includes filter buttons, header row, and position cards.
 */
export function PositionsContentSkeleton({ statusFilter }: PositionsContentSkeletonProps) {
	return (
		<>
			{/* Mobile: filters left, sort right */}
			<div className="mb-3 flex items-center justify-between gap-2 sm:hidden">
				<div className="flex items-center gap-1.5">
					<Skeleton className="h-8 w-[52px] rounded-lg" />
					<Skeleton className="h-8 w-[56px] rounded-lg" />
				</div>
				<Skeleton className="h-8 w-[62px] rounded-lg" />
			</div>

			{/* Desktop: filters row then header row */}
			<div className="hidden sm:block">
				<div className="mb-3 flex items-center gap-1.5">
					<Skeleton className="h-8 w-[52px] rounded-lg" />
					<Skeleton className="h-8 w-[56px] rounded-lg" />
				</div>
				<div className="mb-3 flex items-center gap-3 rounded-lg bg-bg-secondary/50 px-4 py-2">
					{statusFilter === "closed" && <div className="w-16 shrink-0" />}
					{/* Avatar spacer */}
					<div className="size-8 shrink-0" />
					{/* Market column */}
					<div className="min-w-0 flex-1">
						<Skeleton className="h-3 w-12" />
					</div>
					{statusFilter === "active" && (
						<>
							{/* Avg column */}
							<div className="w-16 shrink-0 text-right">
								<Skeleton className="ml-auto h-3 w-6" />
							</div>
							{/* Current column */}
							<div className="w-16 shrink-0 text-right">
								<Skeleton className="ml-auto h-3 w-10" />
							</div>
						</>
					)}
					{/* Value column */}
					<div className="w-28 shrink-0 text-right">
						<Skeleton className="ml-auto h-3 w-10" />
					</div>
					{/* Action spacer */}
					<div className="w-10 shrink-0" />
				</div>
			</div>

			{/* Position cards skeleton */}
			<div className="space-y-3 sm:space-y-4">
				{[0, 1, 2].map(i => (
					<PositionCardSkeleton key={`position-${i}`} />
				))}
			</div>
		</>
	)
}

/**
 * Skeleton for activity tab content.
 * Includes sort control, header row, and activity rows.
 */
export function ActivityContentSkeleton() {
	return (
		<>
			{/* Mobile skeleton - compact sort control */}
			<div className="mb-3 flex items-center justify-end px-1 sm:hidden">
				<Skeleton className="h-[30px] w-[76px] rounded-md" />
			</div>

			{/* Desktop skeleton - full header row */}
			<div className="mb-3 hidden items-center gap-3 rounded-lg bg-bg-secondary/50 px-4 py-2 sm:flex">
				{/* Type column */}
				<div className="w-8 shrink-0">
					<Skeleton className="h-3 w-6" />
				</div>
				{/* Avatar spacer */}
				<div className="size-8 shrink-0" />
				{/* Market column */}
				<div className="min-w-0 flex-1">
					<Skeleton className="h-3 w-12" />
				</div>
				{/* Amount column */}
				<div className="w-20 shrink-0 text-right">
					<Skeleton className="ml-auto h-3 w-12" />
				</div>
				{/* Time column */}
				<div className="w-24 shrink-0 text-right">
					<Skeleton className="ml-auto h-3 w-8" />
				</div>
			</div>

			{/* Activity rows skeleton */}
			<div className="space-y-3 sm:space-y-4">
				{[0, 1, 2].map(i => (
					<ActivityRowSkeleton key={`activity-${i}`} variant="profile" />
				))}
			</div>
		</>
	)
}
